/**
 * Daily series for a chart.
 *
 * The stats endpoints (GET /stats/revenue, /stats/users) answer with only the days that had
 * something in them (`GROUP BY DATE(...)`): a month with two sales is two points. Drawn as they
 * come, two far-apart sales are joined by a straight line (the days in between look like they
 * had sales too), the x axis spaces 25-sep, 16-okt and 17-okt evenly, and a single day is a lone
 * dot. `fillDays` lays the whole period out day by day with 0 where nothing happened, so a chart
 * tells the truth about the quiet days as well.
 *
 *   fillDays([{ date: '2026-09-25', amount: 2494000 }], '30d', 'amount')
 *   // -> 31 points from 2026-09-17 to 2026-10-17, 0 on every day but the 25th
 *
 * The server counts days in UTC (`DATE("paidAt")`), so the days here are UTC calendar days too:
 * the same day keys the API sends, never shifted by the viewer's time zone. A period with no
 * activity at all returns [] so the page can say "nothing in this period" instead of drawing a
 * flat line.
 */

const DAY_MS = 86400000;
const MAX_POINTS = 400;
const PERIOD_DAYS = { '7d': 7, '30d': 30, '90d': 90 };

const dayKey = (ms) => new Date(ms).toISOString().slice(0, 10);
const dayStart = (key) => Date.parse(`${key}T00:00:00.000Z`);

/** First UTC day of a period that ends at `now`: the server's `end - N days` (a year: same date last year). */
export function periodStartKey(period, now = Date.now()) {
  if (period === '1y') {
    const d = new Date(now);
    d.setUTCFullYear(d.getUTCFullYear() - 1);
    return dayKey(d.getTime());
  }
  const days = PERIOD_DAYS[period] ?? PERIOD_DAYS['30d'];
  return dayKey(now - days * DAY_MS);
}

/**
 * @param {Array<{ date: string }>} points  what the API sent: one entry per day that had data
 * @param {'7d'|'30d'|'90d'|'1y'} period    the period the request asked for
 * @param {string} key                      the field that holds the value ('amount', 'count')
 * @param {number} [now]                    clock override for tests
 * @returns {Array<{ date: string, [key]: number }>} one point per UTC day, oldest first
 */
export function fillDays(points, period, key = 'amount', now = Date.now()) {
  const byDay = new Map();
  (Array.isArray(points) ? points : []).forEach((p) => {
    const date = String(p?.date || '').slice(0, 10);
    const value = Number(p?.[key]);
    if (/^\d{4}-\d{2}-\d{2}$/.test(date) && Number.isFinite(value)) {
      byDay.set(date, (byDay.get(date) || 0) + value);
    }
  });
  if (![...byDay.values()].some((v) => v !== 0)) return [];

  // Never drop a day the server sent, even if this clock and the server's disagree by a day.
  const days = [...byDay.keys()].sort();
  const from = [periodStartKey(period, now), days[0]].sort()[0];
  const to = [dayKey(now), days[days.length - 1]].sort().reverse()[0];

  const out = [];
  for (let t = dayStart(from), end = dayStart(to); t <= end && out.length < MAX_POINTS; t += DAY_MS) {
    const date = dayKey(t);
    out.push({ date, [key]: byDay.get(date) || 0 });
  }
  return out;
}

/**
 * Round axis ticks for a chart whose values run from 0 to `max`: 0, 500 000, 1M, 1.5M, 2M, 2.5M
 * instead of the 0, 650k, 1.3M, 1.95M, 2.6M that a charting library divides the range into.
 * The step is 1, 2, 2.5 or 5 times a power of ten, and the last tick is at or above `max`.
 *
 *   const { domain, ticks } = niceTicks(2494000)          // domain [0, 2500000], 6 ticks
 *   <YAxis domain={domain} ticks={ticks} tickFormatter={compactTick} />
 *
 * `intervals` is how many gaps the axis may have at most; `integer` keeps every tick a whole
 * number (a count of people), so a max of 3 gives 0, 1, 2, 3 and never 0.5.
 */
export function niceTicks(max, { intervals = 5, integer = false } = {}) {
  const top = Number(max);
  if (!Number.isFinite(top) || top <= 0) return { domain: [0, integer ? 1 : 100], ticks: integer ? [0, 1] : [0, 100] };

  const rough = top / Math.max(1, intervals);
  const power = 10 ** Math.floor(Math.log10(rough));
  const fraction = rough / power;
  let step = (fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 2.5 ? 2.5 : fraction <= 5 ? 5 : 10) * power;
  if (integer) step = Math.max(1, Math.ceil(step));

  const count = Math.ceil(top / step - 1e-9);
  const ticks = [];
  for (let i = 0; i <= Math.max(1, count); i += 1) ticks.push(Math.round(i * step * 1e6) / 1e6);
  return { domain: [0, ticks[ticks.length - 1]], ticks };
}

